/**
 * Builds one sprite sheet texture from programmatic pixel-art frames (concept: dark vessel + glowing virus / crystals).
 */

import { ATLAS_FRAME } from '../config/gameConfig.js';

const W = ATLAS_FRAME;
const COLS = 8;
const ROWS = 4;

function strokeCircle(ctx, cx, cy, r, color, lineWidth = 2) {
  ctx.strokeStyle = color;
  ctx.lineWidth = lineWidth;
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.stroke();
}

function fillCircle(ctx, cx, cy, r, fill) {
  ctx.fillStyle = fill;
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fill();
}

/** Player: bright green spiky virus with face + soft outer ring (concept). */
function drawPlayer(ctx, cx, cy) {
  const x0 = Math.round(cx);
  const y0 = Math.round(cy);

  ctx.strokeStyle = 'rgba(80,255,140,0.22)';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.arc(x0, y0, 21, 0, Math.PI * 2);
  ctx.stroke();
  ctx.strokeStyle = 'rgba(120,255,180,0.12)';
  ctx.beginPath();
  ctx.arc(x0, y0, 19, 0, Math.PI * 2);
  ctx.stroke();

  ctx.fillStyle = '#136b2e';
  ctx.beginPath();
  ctx.ellipse(x0, y0 + 5, 13, 10, 0, 0, Math.PI * 2);
  ctx.fill();

  const spikes = 11;
  const rOut = 17;
  const rIn = 11.5;
  ctx.fillStyle = '#38e860';
  ctx.beginPath();
  for (let i = 0; i < spikes * 2; i++) {
    const a = (i / (spikes * 2)) * Math.PI * 2 - Math.PI / 2;
    const r = i % 2 === 0 ? rOut : rIn;
    const px = x0 + Math.cos(a) * r;
    const py = y0 + Math.sin(a) * r;
    if (i === 0) ctx.moveTo(px, py);
    else ctx.lineTo(px, py);
  }
  ctx.closePath();
  ctx.fill();

  ctx.fillStyle = '#b6ffca';
  ctx.beginPath();
  ctx.arc(x0 - 6, y0 - 7, 5, 0, Math.PI * 2);
  ctx.fill();

  ctx.strokeStyle = '#062a14';
  ctx.lineWidth = 2;
  ctx.beginPath();
  for (let i = 0; i < spikes * 2; i++) {
    const a = (i / (spikes * 2)) * Math.PI * 2 - Math.PI / 2;
    const r = i % 2 === 0 ? rOut : rIn;
    const px = x0 + Math.cos(a) * r;
    const py = y0 + Math.sin(a) * r;
    if (i === 0) ctx.moveTo(px, py);
    else ctx.lineTo(px, py);
  }
  ctx.closePath();
  ctx.stroke();

  ctx.fillStyle = '#f4fffa';
  ctx.fillRect(x0 - 8, y0 - 3, 3, 3);
  ctx.fillRect(x0 + 5, y0 - 3, 3, 3);
  ctx.fillStyle = '#1a4028';
  ctx.fillRect(x0 - 7, y0 - 2, 1, 1);
  ctx.fillRect(x0 + 6, y0 - 2, 1, 1);

  ctx.strokeStyle = '#1a4028';
  ctx.lineWidth = 1.2;
  ctx.beginPath();
  ctx.arc(x0, y0 + 6, 4, 0.2 * Math.PI, 0.8 * Math.PI);
  ctx.stroke();
}

/** Collectible: golden “ally” virus blob. */
function drawCollectible(ctx, cx, cy) {
  const x0 = Math.round(cx);
  const y0 = Math.round(cy);
  ctx.fillStyle = '#c89010';
  ctx.beginPath();
  const n = 9;
  for (let i = 0; i < n * 2; i++) {
    const a = (i / (n * 2)) * Math.PI * 2 - Math.PI / 2;
    const r = i % 2 === 0 ? 12.5 : 8.5;
    const px = x0 + Math.cos(a) * r;
    const py = y0 + Math.sin(a) * r;
    if (i === 0) ctx.moveTo(px, py);
    else ctx.lineTo(px, py);
  }
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = '#fff1a8';
  ctx.beginPath();
  ctx.arc(x0 - 4, y0 - 4, 4, 0, Math.PI * 2);
  ctx.fill();
  strokeCircle(ctx, x0, y0, 10, '#6a5008', 2);
}

/** WBC: large pale spiky sphere, unfriendly eyes. */
function drawWBC(ctx, cx, cy) {
  const x0 = Math.round(cx);
  const y0 = Math.round(cy);
  ctx.fillStyle = '#dcd8e8';
  const spikes = 13;
  const rOut = 15.5;
  const rIn = 11;
  ctx.beginPath();
  for (let i = 0; i < spikes * 2; i++) {
    const a = (i / (spikes * 2)) * Math.PI * 2 - Math.PI / 2;
    const r = i % 2 === 0 ? rOut : rIn;
    const px = x0 + Math.cos(a) * r;
    const py = y0 + Math.sin(a) * r;
    if (i === 0) ctx.moveTo(px, py);
    else ctx.lineTo(px, py);
  }
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = '#9a9aaf';
  ctx.lineWidth = 2;
  ctx.stroke();
  ctx.fillStyle = '#6a6878';
  ctx.beginPath();
  ctx.ellipse(x0 - 5, y0 - 2, 3.5, 4.2, -0.2, 0, Math.PI * 2);
  ctx.ellipse(x0 + 5, y0 - 1, 3.2, 4, 0.15, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#202028';
  ctx.beginPath();
  ctx.arc(x0 - 5, y0 - 2, 1.5, 0, Math.PI * 2);
  ctx.arc(x0 + 5, y0 - 1, 1.4, 0, Math.PI * 2);
  ctx.fill();
}

/** Antibody: crystalline blue shard. */
function drawAntibody(ctx, cx, cy) {
  const x0 = Math.round(cx);
  const y0 = Math.round(cy);
  ctx.fillStyle = '#7ec8ff';
  ctx.strokeStyle = '#2866a0';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(x0, y0 - 16);
  ctx.lineTo(x0 + 13, y0 + 4);
  ctx.lineTo(x0 + 5, y0 + 14);
  ctx.lineTo(x0 - 5, y0 + 14);
  ctx.lineTo(x0 - 13, y0 + 4);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  ctx.strokeStyle = 'rgba(255,255,255,0.55)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(x0, y0 - 12);
  ctx.lineTo(x0 - 6, y0 + 2);
  ctx.moveTo(x0 - 3, y0 - 6);
  ctx.lineTo(x0 + 8, y0 + 3);
  ctx.stroke();
}

/** Platelet: pink disc / micro-clot. */
function drawPlatelet(ctx, cx, cy) {
  const x0 = Math.round(cx);
  const y0 = Math.round(cy);
  ctx.fillStyle = '#e8a0b0';
  ctx.beginPath();
  ctx.ellipse(x0, y0, 15, 9, 0.35, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = '#985868';
  ctx.lineWidth = 2;
  ctx.stroke();
  ctx.fillStyle = '#ffd0dc';
  ctx.beginPath();
  ctx.ellipse(x0 - 4, y0 - 3, 6, 3.5, 0.35, 0, Math.PI * 2);
  ctx.fill();
}

/** Small faceted crystal for power-ups. */
function drawCrystal(ctx, cx, cy, core, rim, facet) {
  const x0 = Math.round(cx);
  const y0 = Math.round(cy);
  ctx.fillStyle = core;
  ctx.strokeStyle = rim;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(x0, y0 - 14);
  ctx.lineTo(x0 + 11, y0 - 4);
  ctx.lineTo(x0 + 9, y0 + 10);
  ctx.lineTo(x0 - 9, y0 + 10);
  ctx.lineTo(x0 - 11, y0 - 4);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  ctx.strokeStyle = facet;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(x0, y0 - 12);
  ctx.lineTo(x0, y0 + 8);
  ctx.moveTo(x0 - 7, y0);
  ctx.lineTo(x0 + 7, y0 - 2);
  ctx.stroke();
}

function drawParticle(ctx, cx, cy) {
  const x0 = Math.round(cx);
  const y0 = Math.round(cy);
  ctx.fillStyle = 'rgba(232,112,138,0.75)';
  ctx.beginPath();
  ctx.ellipse(x0, y0, 7, 4, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = 'rgba(255,200,212,0.45)';
  ctx.beginPath();
  ctx.ellipse(x0 - 2, y0 - 1, 3, 1.8, 0, 0, Math.PI * 2);
  ctx.fill();
}

const DRAWERS = [
  drawPlayer,
  drawCollectible,
  drawWBC,
  drawAntibody,
  drawPlatelet,
  (ctx, cx, cy) => drawCrystal(ctx, cx, cy, '#7ef0ff', '#206a90', 'rgba(255,255,255,0.65)'),
  (ctx, cx, cy) => drawCrystal(ctx, cx, cy, '#ffd85a', '#a06010', 'rgba(255,255,255,0.6)'),
  (ctx, cx, cy) => drawCrystal(ctx, cx, cy, '#d8a8ff', '#603888', 'rgba(255,255,255,0.55)'),
  (ctx, cx, cy) => drawCrystal(ctx, cx, cy, '#9affe0', '#208868', 'rgba(255,255,255,0.55)'),
  drawParticle,
];

/**
 * Register `game-atlas` sprite sheet on the given Phaser scene's texture manager.
 * @param {Phaser.Scene} scene
 */
export function registerGameAtlas(scene) {
  const canvas = document.createElement('canvas');
  canvas.width = COLS * W;
  canvas.height = ROWS * W;
  const ctx = canvas.getContext('2d');
  ctx.imageSmoothingEnabled = false;

  let i = 0;
  for (let row = 0; row < ROWS; row++) {
    for (let col = 0; col < COLS; col++) {
      const drawer = DRAWERS[i];
      if (drawer) {
        const cxi = col * W + W / 2;
        const cyi = row * W + W / 2;
        drawer(ctx, cxi, cyi);
      }
      i++;
    }
  }

  scene.textures.addSpriteSheet('game-atlas', canvas, {
    frameWidth: W,
    frameHeight: W,
  });
}

/** Frame indices in the sheet (stable order) */
export const Frames = {
  PLAYER: 0,
  COLLECTIBLE: 1,
  WBC: 2,
  ANTIBODY: 3,
  PLATELET: 4,
  POWER_SHIELD: 5,
  POWER_SPEED: 6,
  POWER_INVIS: 7,
  POWER_SLOW: 8,
  PARTICLE: 9,
};
